import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError, type SessionRow } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Alert, Button, PageTitle, Panel, Pill } from "../components/ui";
import { Monitor, Tablet } from "lucide-react";

/** "3 minutes ago" reads better than a timestamp for an at-a-glance list. */
function relative(iso: string): string {
  const seconds = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? "yesterday" : `${days} days ago`;
}

/** Full UA strings are noise in a table; the browser name is what identifies it. */
function describe(row: SessionRow): string {
  if (row.label) return row.label;
  const ua = row.userAgent ?? "";
  if (row.kind === "device") return "Aircraft iPad";
  for (const [needle, name] of [
    ["Firefox", "Firefox"],
    ["Edg/", "Edge"],
    ["Chrome", "Chrome"],
    ["Safari", "Safari"],
  ] as const) {
    if (ua.includes(needle)) return name;
  }
  return "Unknown browser";
}

export function Sessions() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [rows, setRows] = useState<SessionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  async function load() {
    try {
      setRows((await api.sessions()).sessions);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't load devices.");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function revoke(row: SessionRow) {
    setError(null);
    setRevoking(row.id);
    try {
      await api.revokeSession(row.id);
      // Signing out the session you are using ends the visit, so leave rather
      // than re-rendering a list you can no longer fetch.
      if (row.current) {
        await signOut();
        navigate("/login", { replace: true });
        return;
      }
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't sign that device out.");
    } finally {
      setRevoking(null);
    }
  }

  return (
    <div className="flex flex-col gap-8">
      <PageTitle detail="Browsers and iPads currently signed in to this account.">
        Signed-in devices
      </PageTitle>

      {error && <Alert>{error}</Alert>}

      <Panel>
        {rows === null ? (
          <p className="p-6 text-[0.95rem] text-ink-soft">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-[0.95rem] text-ink-soft">No active sessions.</p>
        ) : (
          <ul className="divide-y divide-rule-soft">
            {rows.map((row) => (
              <li key={row.id} className="flex items-center gap-4 px-5 py-4">
                <span className="grid place-items-center w-11 h-11 rounded-lg bg-muted/15 text-ink-soft shrink-0" aria-hidden>
                  {row.kind === "device" ? <Tablet size={22} strokeWidth={2.2} /> : <Monitor size={22} strokeWidth={2.2} />}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <span className="text-[1rem] font-semibold text-ink">{describe(row)}</span>
                    {row.current && <Pill className="bg-accent/20 text-accent-text">This device</Pill>}
                  </div>
                  <p className="mt-0.5 text-[0.9rem] text-ink-soft truncate">
                    {row.ipAddress ?? "unknown address"} · active {relative(row.lastSeenAt)}
                  </p>
                </div>
                <Button
                  variant="destructive"
                  busy={revoking === row.id}
                  onClick={() => revoke(row)}
                >
                  Sign out
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
